import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import authService from '@/services/authService.ts';
import Logo from '@/components/shared/Logo.tsx';
import FormError from '@/components/shared/FormError.tsx';
import {
  createForgotPasswordSchema,
  type ForgotPasswordFormValues,
} from '@/schemas/forgotPasswordSchema.ts';

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [sent, setSent] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const schema = useMemo(() => createForgotPasswordSchema(t), [t]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: ForgotPasswordFormValues) => {
    setSubmitError(null);
    try {
      await authService.forgotPassword({ email: data.email });
      setSent(true);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : t('auth.forgotPasswordError'));
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-card px-4">
      <div className="w-full max-w-md">
        <div className="rounded-xl border border-border bg-card p-8 shadow-sm">
          <div className="mb-8 flex flex-col items-center gap-3">
            <Logo size="lg" />
            <p className="text-sm text-muted-foreground">
              {t('auth.forgotPasswordSubtitle')}
            </p>
          </div>

          {sent ? (
            <div className="text-center">
              <p className="text-sm text-foreground">
                {t('auth.forgotPasswordSent')}
              </p>
              <Link
                to="/login"
                className="mt-6 inline-block text-sm font-medium text-primary hover:text-primary-hover transition-colors no-underline"
              >
                {t('auth.forgotPasswordBack')}
              </Link>
            </div>
          ) : (
            <>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                <div>
                  <label
                    htmlFor="email"
                    className="mb-1.5 block text-sm font-medium text-foreground"
                  >
                    {t('auth.emailLabel')}
                  </label>
                  <input
                    id="email"
                    type="email"
                    placeholder={t('auth.emailPlaceholder')}
                    autoComplete="email"
                    className="w-full rounded-lg border border-border bg-card px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
                    {...register('email')}
                  />
                  {errors.email && (
                    <p className="mt-1 text-xs text-destructive">{errors.email.message}</p>
                  )}
                </div>

                <FormError
                  message={submitError}
                  onDismiss={() => setSubmitError(null)}
                />

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? t('auth.forgotPasswordSubmitting') : t('auth.forgotPasswordSubmit')}
                </button>
              </form>

              <p className="mt-6 text-center text-sm text-muted-foreground">
                <Link
                  to="/login"
                  className="font-medium text-primary hover:text-primary-hover transition-colors no-underline"
                >
                  {t('auth.forgotPasswordBack')}
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
